/*
  Kiwi Ears · ambient orb background (Figma glow layers 221:187 / 221:188, made dynamic)
  Three.js renders soft radial-gradient orbs; GSAP drives slow drift / breathe / opacity
  so the colours keep recombining. Brand colours: #00E58A · #6B5CE7 · #4B7BF5.
  Respects prefers-reduced-motion (static glows, no animation loop).
*/
import * as THREE from 'three';

(function () {
  const canvas = document.getElementById('kiwi-orbs');
  if (!canvas) return;

  const BASE = '#F4FCF8'; // Rene 2026-08-14: brand canvas rgba(232,250,242,.5) composited on white
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false, powerPreference: 'low-power' });
  } catch (e) {
    // WebGL unavailable — approximate the Figma glows with static CSS gradients
    canvas.style.background =
      'radial-gradient(48% 48% at 8% 12%, rgba(0,229,138,.30), transparent 70%),' +
      'radial-gradient(44% 44% at 12% 88%, rgba(75,123,245,.24), transparent 70%),' +
      'radial-gradient(52% 52% at 92% 10%, rgba(107,92,231,.28), transparent 70%),' + BASE;
    return;
  }
  renderer.setClearColor(new THREE.Color(BASE), 1);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));

  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
  camera.position.z = 1;

  // One shared unit-plane geometry; each orb is a plane with a radial-falloff shader.
  const geometry = new THREE.PlaneGeometry(2, 2);
  const frag = `
    precision mediump float;
    varying vec2 vUv;
    uniform vec3 uColor;
    uniform float uAlpha;
    void main() {
      float d = distance(vUv, vec2(0.5));
      float a = pow(1.0 - smoothstep(0.0, 0.5, d), 2.0) * uAlpha;
      gl_FragColor = vec4(uColor, a);
    }`;
  const vert = `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }`;

  // x/y in viewport fractions (0..1 from top-left), r = radius as fraction of the larger side
  const ORBS = [
    { color: '#00E58A', x: 0.06, y: 0.16, r: 0.42, alpha: 0.46, drift: 0.20, dur: 12 }, // green — top left (sketch)
    { color: '#4B7BF5', x: 0.12, y: 0.90, r: 0.38, alpha: 0.36, drift: 0.24, dur: 14 }, // blue — bottom left (sketch)
    { color: '#6B5CE7', x: 0.94, y: 0.10, r: 0.44, alpha: 0.40, drift: 0.20, dur: 13 }, // purple — top right (sketch)
    { color: '#00E58A', x: 0.55, y: 1.02, r: 0.24, alpha: 0.26, drift: 0.30, dur: 10 }, // roaming green accent
  ];

  let W = 0, H = 0;
  const meshes = ORBS.map((o) => {
    const material = new THREE.ShaderMaterial({
      vertexShader: vert,
      fragmentShader: frag,
      uniforms: {
        uColor: { value: new THREE.Color(o.color) },
        uAlpha: { value: o.alpha },
      },
      transparent: true,
      depthWrite: false,
      depthTest: false,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData = o;
    scene.add(mesh);
    return mesh;
  });

  function layout() {
    W = window.innerWidth; H = window.innerHeight;
    renderer.setSize(W, H, false);
    camera.left = -W / 2; camera.right = W / 2;
    camera.top = H / 2; camera.bottom = -H / 2;
    camera.updateProjectionMatrix();
    meshes.forEach((m) => {
      const o = m.userData;
      const R = o.r * Math.max(W, H);
      m.position.set((o.x - 0.5) * W, (0.5 - o.y) * H, 0);
      m.scale.set(R, R, 1);
      m.userData.baseScale = R;
    });
  }
  layout();
  window.addEventListener('resize', () => { layout(); render(); });

  function render() { renderer.render(scene, camera); }

  if (reduceMotion || !window.gsap) { render(); return; } // static glow layers, exactly like the Figma frames

  const gsap = window.gsap;
  /* Organic "lights moving behind the content" (Rene 2026-08-14): instead of a
     fixed yoyo, every orb wanders to a fresh random point around its anchor each
     leg — continuous, clearly visible, never repeating. Breathing is elliptical
     (x and y desynced) and brightness surges on its own clock. */
  let wanderTweens = [];
  function animate() {
    wanderTweens.forEach((t) => t.kill());
    wanderTweens = [];
    meshes.forEach((m, i) => {
      const o = m.userData;
      const ax = (o.x - 0.5) * W, ay = (0.5 - o.y) * H;
      const rx = o.drift * W, ry = o.drift * H;
      function wander() {
        const t = gsap.to(m.position, {
          x: ax + gsap.utils.random(-rx, rx),
          y: ay + gsap.utils.random(-ry, ry),
          duration: gsap.utils.random(o.dur * 0.3, o.dur * 0.6),
          ease: 'sine.inOut',
          onComplete: wander,
        });
        wanderTweens.push(t);
      }
      wander();
      wanderTweens.push(gsap.to(m.scale, { x: '*=1.5', duration: o.dur * 0.42, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: -i * 2.3 }));
      wanderTweens.push(gsap.to(m.scale, { y: '*=1.34', duration: o.dur * 0.31, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: -i * 1.4 }));
      wanderTweens.push(gsap.to(m.material.uniforms.uAlpha, { value: o.alpha * 1.75, duration: o.dur * 0.36, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: -i * 1.7 }));
    });
  }
  animate();
  let rsT;
  window.addEventListener('resize', () => { clearTimeout(rsT); rsT = setTimeout(animate, 250); });
  // scroll parallax — orbs slide gently as the page scrolls (Lenis drives scroll globally)
  if (window.ScrollTrigger) {
    meshes.forEach((m, i) => {
      gsap.to(m.position, {
        y: `+=${(i % 2 ? -1 : 1) * H * 0.18}`,
        ease: 'none',
        scrollTrigger: { trigger: document.body, start: 'top top', end: 'max', scrub: 1.2 },
      });
    });
  }
  gsap.ticker.add(render); // rAF-driven; pauses automatically when the tab is hidden
})();
